import React, { useState } from "react";
import { ref, set } from "firebase/database";
import { v4 as uuidv4 } from "uuid"; // ✅ Import UUID
import { db } from "../services/firebase";
import "./AdminCodingUpload.css";

function AdminCodingUpload({ user }) {
  const [quizId, setQuizId] = useState("");
  const [quizTitle, setQuizTitle] = useState("");
  const [topic, setTopic] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [countdown, setCountdown] = useState("30");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [allowReattempt, setAllowReattempt] = useState(false);
  const [showResultAfterSubmit, setShowResultAfterSubmit] = useState(true);
  const [delayedResultTime, setDelayedResultTime] = useState("0");

  const [questions, setQuestions] = useState([]);
  const [showPanel, setShowPanel] = useState(false);

  const addQuestion = () => {
    setQuestions([
      ...questions,
      {
        title: "",
        description: "",
        marks: 0,
        testCases: [{ input: "", expectedOutput: "", withLength: false }],
      },
    ]);
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    setQuestions(updated);
  };

  const updateTestCase = (qIndex, tIndex, field, value) => {
    const updated = [...questions];
    updated[qIndex].testCases[tIndex][field] = value;
    setQuestions(updated);
  };

  const addTestCase = (qIndex) => {
    const updated = [...questions];
    updated[qIndex].testCases.push({ input: "", expectedOutput: "", withLength: false });
    setQuestions(updated);
  };

  const handleUpload = async () => {
    const generatedQuizId = quizId || uuidv4(); // ✅ Use existing or generate new

    if (!quizTitle || !topic || questions.length === 0) {
      alert("Please fill in all fields and add at least one question.");
      return;
    }

    const payload = {
      quizTitle,
      topic,
      createdBy: user.email,
      date,
      countdown,
      startTime: startTime || null,
      endTime: endTime || null,
      allowReattempt,
      showResultAfterSubmit,
      delayedResultTime: showResultAfterSubmit ? "0" : delayedResultTime,
      questions: {},
    };

    questions.forEach((q, i) => {
      const qCopy = { ...q, testCases: [] };

      q.testCases.forEach((t) => {
        let processedInput = t.input.trim();
        const values = processedInput.split(/\s+/);
        const isAllNumbers = values.every((v) => /^-?\d+$/.test(v));

        if (t.withLength && !processedInput.includes("\n") && isAllNumbers && values.length > 1) {
          processedInput = `${values.length}\n${values.join(" ")}`;
        }

        qCopy.testCases.push({
          input: processedInput,
          expectedOutput: t.expectedOutput,
        });
      });

      payload.questions[`q${i + 1}`] = qCopy;
    });

    try {
      await set(ref(db, `codingQuestions/${generatedQuizId}`), payload); // ✅ Use UUID
      alert("✅ Coding quiz uploaded!");
      setQuizId(generatedQuizId); // ✅ Keep the UUID in input
      setQuizTitle("");
      setTopic("");
      setQuestions([]);
    } catch (error) {
      console.error(error);
      alert("Upload failed.");
    }
  };

  return (
    <div className="coding-upload-wrapper">
      <h2>🧠 Admin Coding Upload Panel</h2>
      <button  id="admin-code-btn"className="submit-btn" onClick={() => setShowPanel(!showPanel)}>
        {showPanel ? "🙈 Hide Coding Upload" : "📂 Show Coding Upload"}
      </button>

      {showPanel && (
        <div className="coding-upload">
          <input
            type="text"
            placeholder="Quiz ID (auto-generated if empty)"
            value={quizId}
            onChange={(e) => setQuizId(e.target.value)}
          />
          <input
            type="text"
            placeholder="Quiz Title"
            value={quizTitle}
            onChange={(e) => setQuizTitle(e.target.value)}
          />
          <input
            type="text"
            placeholder="Topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
          />
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <input
            type="number"
            placeholder="Countdown (minutes)"
            value={countdown}
            onChange={(e) => setCountdown(e.target.value)}
          />
          <label>Start Time (optional)</label>
          <input
            type="datetime-local"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
          <label>End Time (optional)</label>
          <input
            type="datetime-local"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
          />
          <label>
            <input
              type="checkbox"
              checked={allowReattempt}
              onChange={(e) => setAllowReattempt(e.target.checked)}
            />
            Allow Reattempt
          </label>
          <label>
            <input
              type="checkbox"
              checked={showResultAfterSubmit}
              onChange={(e) => setShowResultAfterSubmit(e.target.checked)}
            />
            Show Result Immediately After Submit
          </label>
          {!showResultAfterSubmit && (
            <input
              type="number"
              placeholder="Delayed Result Time (minutes)"
              value={delayedResultTime}
              onChange={(e) => setDelayedResultTime(e.target.value)}
            />
          )}

          <hr />
          <h4>Questions</h4>
          {questions.map((q, qIndex) => (
            <div key={qIndex} className="coding-question-block">
              <input
                type="text"
                placeholder="Question Title"
                value={q.title}
                onChange={(e) => updateQuestion(qIndex, "title", e.target.value)}
              />
              <textarea
                placeholder="Description"
                rows={4}
                value={q.description}
                onChange={(e) => updateQuestion(qIndex, "description", e.target.value)}
              />
              <input
                type="number"
                placeholder="Marks"
                value={q.marks}
                onChange={(e) =>
                  updateQuestion(qIndex, "marks", parseInt(e.target.value) || 0)
                }
              />

              <h5>Test Cases</h5>
              {q.testCases.map((t, tIndex) => (
                <div key={tIndex} className="test-case">
                  <input
                    type="text"
                    placeholder="Input"
                    value={t.input}
                    onChange={(e) =>
                      updateTestCase(qIndex, tIndex, "input", e.target.value)
                    }
                  />
                  <input
                    type="text"
                    placeholder="Expected Output"
                    value={t.expectedOutput}
                    onChange={(e) =>
                      updateTestCase(qIndex, tIndex, "expectedOutput", e.target.value)
                    }
                  />
                  <label>
                    <input
                      type="checkbox"
                      checked={t.withLength}
                      onChange={(e) =>
                        updateTestCase(qIndex, tIndex, "withLength", e.target.checked)
                      }
                    />
                    Add size prefix
                  </label>
                </div>
              ))}
              <button  id="admin-code-btn" onClick={() => addTestCase(qIndex)}>➕ Add Test Case</button>
              <hr />
            </div>
          ))}

          <button  id="admin-code-btn"onClick={addQuestion}>➕ Add Question</button>
          <br />
          <button  id="admin-code-btn" onClick={handleUpload}>🚀 Upload codingQuestions</button>
        </div>
      )}
    </div>
  );
}

export default AdminCodingUpload;
